"use client";

import React, { useState } from "react";
import { cn } from "utils/utils";
import { CardPreviewSizing } from "utils/styles";
import { SizingOptions } from "modules/common/components/CardPreviewLoading";
import { useCardByDataId } from "modules/cards/hooks/useCardByDataId";
import {
  getSelectionState,
  useMultiSelectStore,
} from "modules/common/stores/useMultiSelectStore";
import { InteractiveCardItem } from "./types";
import {
  CardGridItemProvider,
  SelectionContextValue,
} from "./CardGridItemContext";

export type SelectionMode =
  | { type: "none" }
  | { type: "store" }
  | {
      type: "controlled";
      selected: boolean;
      onToggle: () => void;
      isAnySelected: boolean;
      /**
       * Set when `onToggle` routes back into the multi-select store while
       * "select all" is active, so it clears everything rather than dropping
       * this one card. Controlled callers that own their own selection state
       * (the proposal picker) leave it false.
       */
      clearsAll?: boolean;
    };

type RingVariant = "primary" | "secondary" | "warning";

const RING_CLASSES: Record<RingVariant, string> = {
  primary: "border-primary",
  secondary: "border-secondary",
  warning: "border-warning",
};

const NOOP_SELECTION: SelectionContextValue = {
  state: "none",
  isAnySelected: false,
  onToggle: () => {},
  modeType: "none",
  clearsAll: false,
};

interface CardGridItemRootProps {
  item: InteractiveCardItem;
  sizing?: SizingOptions;
  selectionMode?: SelectionMode;
  ringVariant?: RingVariant;
  className?: string;
  style?: React.CSSProperties;
  /**
   * Whether a panel/drawer renders below the card image. The selection ring
   * traces the cell's outer silhouette, so the bottom corners use the panel's
   * box radius when a panel is present, but must match the card image's own
   * `4.75%/3.5%` radius when the cell is image-only (e.g. the proposal picker
   * hides the panel) — otherwise the ring bulges past the card's bottom corners.
   */
  hasPanel?: boolean;
  children: React.ReactNode;
}

/** Subscribes to multi-select store only when selectionMode is "store". */
function StoreSelectionProvider({
  item,
  children,
}: {
  item: InteractiveCardItem;
  children: (sel: SelectionContextValue) => React.ReactNode;
}) {
  const selectAllActive = useMultiSelectStore((s) => s.selectAllActive);
  const storeState = useMultiSelectStore((s) =>
    getSelectionState(s.selectedIds, item.ids)
  );
  const isAnySelected = useMultiSelectStore((s) => s.hasSelection());
  const toggleItem = useMultiSelectStore((s) => s.toggleItem);

  return children({
    // Under "select all" the store deliberately holds no ids, but every card
    // in the grid *is* selected. Report that, so the selection ring and the
    // ContactActions aria-label agree with the checkbox SelectionCheckbox
    // already paints checked.
    state: selectAllActive ? "all" : storeState,
    isAnySelected,
    onToggle: () => toggleItem(item.ids),
    modeType: "store",
    clearsAll: selectAllActive,
  });
}

function deriveControlledSelection(
  mode: Extract<SelectionMode, { type: "controlled" }>
): SelectionContextValue {
  return {
    state: mode.selected ? "all" : "none",
    isAnySelected: mode.isAnySelected,
    onToggle: mode.onToggle,
    modeType: "controlled",
    clearsAll: mode.clearsAll ?? false,
  };
}

export function CardGridItemRoot({
  item,
  sizing = CardPreviewSizing,
  selectionMode = { type: "none" },
  ringVariant = "primary",
  className,
  style,
  hasPanel = true,
  children,
}: CardGridItemRootProps) {
  const [shouldFetch, setShouldFetch] = useState(false);
  useCardByDataId({
    dataId: item.dataId,
    game: item.game,
    enabled: shouldFetch,
  });

  const renderContent = (selection: SelectionContextValue) => (
    <CardGridItemProvider
      value={{
        item,
        sizing,
        selection,
        onPrefetchTrigger: () => setShouldFetch(true),
      }}
    >
      <div
        className={cn(
          "group relative grow",
          // Bottom corners follow whatever sits at the bottom of the cell:
          // the panel's box radius when a panel renders, else the card
          // image's own radius so the selection ring hugs the card.
          hasPanel
            ? "rounded-bl-box rounded-br-box"
            : "rounded-bl-[4.75%_3.5%] rounded-br-[4.75%_3.5%]",
          selection.state !== "none" && "z-20",
          className
        )}
        style={style}
        onMouseEnter={() => setShouldFetch(true)}
        onFocus={() => setShouldFetch(true)}
      >
        {children}
        {selection.state !== "none" && (
          <SelectionRing hasPanel={hasPanel} variant={ringVariant} />
        )}
      </div>
    </CardGridItemProvider>
  );

  if (selectionMode.type === "store") {
    return (
      <StoreSelectionProvider item={item}>
        {renderContent}
      </StoreSelectionProvider>
    );
  }

  if (selectionMode.type === "controlled") {
    return renderContent(deriveControlledSelection(selectionMode));
  }

  return renderContent(NOOP_SELECTION);
}

/**
 * The selection frame, drawn as an overlay INSIDE the cell rather than as a
 * `ring` around it. A ring is an outset box-shadow: on every top row of every
 * selectable grid it fell outside the scroll container, which clips there, so
 * its top edge was cut off — and the only way to give it room was to push the
 * cards down, which knocked them out of line with the facet sidebar and the
 * table view beside them.
 *
 * It has to be a real element: `ring-inset` is an inset box-shadow and
 * `-outline-offset` is an outline, and Chrome paints both of those beneath the
 * element's in-flow descendants, so the card image would cover the frame.
 * A positioned overlay paints above it.
 *
 * Radii match the root's exactly (same box, so the percentages resolve the
 * same), which is what keeps the frame tracing the cell's silhouette.
 */
function SelectionRing({
  hasPanel,
  variant,
}: {
  hasPanel: boolean;
  variant: RingVariant;
}) {
  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-0 z-30 border-2",
        "rounded-tl-[4.75%_3.5%] rounded-tr-[4.75%_3.5%]",
        hasPanel
          ? "rounded-bl-box rounded-br-box"
          : "rounded-bl-[4.75%_3.5%] rounded-br-[4.75%_3.5%]",
        RING_CLASSES[variant]
      )}
    />
  );
}
